/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { memo, useRef } from 'react';
import { Box, IconButton } from '@mui/material';
import { useSetAtom } from 'jotai';
import useQuery from '../../../../../../shared/hooks/useQuery';
import { QSTypography } from '../../../../../../shared/components/atoms';
import OverflowTooltip from '../../../../../../shared/components/molecules/overflow-tooltip';
import CloseIcon from '../../../../../../shared/icons/icons-revamp/close';
import { customFieldConditionsAtom } from '../../../states/chat-list';
import { customFieldsAtom } from '../../../states/custom-fields';
import { getAllCustomFields } from '../../../requests/get-custom-fields';
import { customFieldValuePickerAtom } from './state';
import { useFilterMetadata } from './hooks/use-filter-metadata';
import { CustomFieldCondition } from './typings';
import { formatValueSummary, getCustomFieldOptionName } from './utils';

interface Props {
  condition: CustomFieldCondition;
  index: number;
}

const AppliedRow = ({ condition, index }: Props) => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const setValuePicker = useSetAtom(customFieldValuePickerAtom);
  const setConditions = useSetAtom(customFieldConditionsAtom);
  const { requestData: metadataRequest } = useFilterMetadata(false);
  const isSelectType = condition.type === 'single_select' || condition.type === 'multi_select';
  const { requestData: customFieldsRequest } = useQuery({
    requestAtom: customFieldsAtom,
    queryFunction: getAllCustomFields,
    autoFetch: isSelectType,
    getCachedData: true,
  });

  const field = (metadataRequest.data?.fields ?? []).find((f) => f.id === condition.id);
  const operatorMeta = (metadataRequest.data?.dataTypeOperator?.[condition.type] ?? []).find(
    (o) => o.type === condition.operator
  );

  const fieldName = field?.name ?? condition.id;
  const operatorLabel = operatorMeta?.name ?? condition.operator;
  const valueSummary = formatValueSummary(condition, (optionId) =>
    getCustomFieldOptionName(customFieldsRequest.data, condition.id, optionId)
  );

  const handleOpen = () => {
    if (!anchorRef.current) return;
    setValuePicker({ anchor: anchorRef.current, fieldId: condition.id, editIndex: index });
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConditions({ type: 'remove', index });
  };

  return (
    <Box
      ref={anchorRef}
      onClick={handleOpen}
      css={css`
        display: inline-flex;
        align-self: flex-start;
        align-items: center;
        margin: 0 16px 6px 16px;
        max-width: calc(100% - 32px);
        min-height: 28px;
        border: 1px solid rgba(30, 41, 59, 0.14);
        border-radius: 4px;
        overflow: hidden;
        cursor: pointer;
        background: #ffffff;
        &:hover {
          border-color: rgba(122, 122, 122, 0.5);
        }
      `}
    >
      <QSTypography
        css={css`
          color: #7a7a7a;
          font-family: Inter;
          font-size: 12px;
          font-weight: 600;
          line-height: 19.6px;
          background-color: #f6f6f6;
          padding: 4px 8px;
          border-right: 1px solid rgba(224, 224, 234, 0.5);
          max-width: 120px;
          flex-shrink: 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        `}
      >
        <OverflowTooltip>{fieldName}</OverflowTooltip>
      </QSTypography>
      <QSTypography
        css={css`
          font-size: 13px;
          color: #7a7a7a;
          font-weight: 500;
          padding: 0 8px;
          flex-shrink: 0;
          white-space: nowrap;
        `}
      >
        {operatorLabel}
      </QSTypography>
      {valueSummary.length > 0 && (
        <QSTypography
          css={css`
            font-size: 13px;
            color: #2d2d2d;
            font-weight: 500;
            padding-right: 8px;
            flex: 1;
            min-width: 0;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          `}
        >
          <OverflowTooltip>{valueSummary}</OverflowTooltip>
        </QSTypography>
      )}
      <IconButton
        disableRipple
        onClick={handleRemove}
        css={css`
          padding: 4px;
          width: 28px;
          height: 28px;
          border-radius: 0;
          border-left: 1px solid rgba(224, 224, 234, 0.5);
          &:hover {
            background-color: rgba(0, 0, 0, 0.06);
          }
        `}
      >
        <CloseIcon size={16} color="#7A7A7A" />
      </IconButton>
    </Box>
  );
};

export default memo(AppliedRow);
